// MARK://dhk8/pos/features/security/pin-gate-store
// Tiny store that backs the manager-PIN gate. A single <PinPromptSheet> is mounted at
// the shell level (PosOverlays); any handler calls `requestManagerPin(action)` which
// opens that sheet and returns a Promise<boolean> (true = approved, false = cancelled).
// Validation: in a demo build (PIN_GATE_DEMO_MODE) the fixed DEMO_MANAGER_PIN is
// accepted; in real builds we POST to dhk8-platform-auth (TODO — see verifyManagerPin).

import { create } from 'zustand'
import { apiFetch, svcUrl } from '../../lib/api.js'
import { useAuthStore } from '../../stores/auth-store.js'
import {
  DEMO_MANAGER_PIN, PIN_GATE_DEMO_MODE, type GatedAction,
} from '../../lib/security-policy.js'

/** TCK-0016: roles de gerencia que tienen las capacidades directo (sin PIN).
 *  Espejo de los roles con gate === true en sdk-rbac (owner/admin/manager). */
const MANAGER_ROLES = new Set(['owner', 'admin', 'manager'])

/** Lane #172 — opciones que el caller puede pasar para personalizar el prompt:
 *  - `requireReason`: pide un motivo libre (textarea) además del PIN. */
export interface PinGateOptions {
  requireReason?: boolean
  /** Texto extra mostrado bajo el subtítulo (ej: "Mesa M5 — Orden #1023"). */
  contextLabel?: string
}

/** Resultado entregado por la prompt: PIN aprobado + razón (si se pidió). */
export interface PinGateResult {
  approved: boolean
  /** Pinned manager pinId — para audit. Sólo presente si approved=true. */
  pinId?: string
  /** Razón si `requireReason: true`. */
  reason?: string
  /** TCK-0016: token de autorización de gerente emitido por manager-verify.
   *  Se manda a la mutation gateada (managerAuthToken) para que el backend
   *  verifique la autorización de verdad (no un literal). undefined en demo. */
  managerAuthToken?: string
}

interface PinGateState {
  open: boolean
  action: GatedAction | null
  options: PinGateOptions
  error: string | null
  busy: boolean
  /** internal — resolver of the in-flight requestManagerPin() promise. */
  _resolve: ((result: PinGateResult) => void) | null
  /** Open the prompt for `action`; resolves with {approved, pinId?, reason?}. */
  request: (action: GatedAction, options?: PinGateOptions) => Promise<PinGateResult>
  /** Try (pinId+pin) — on success closes + resolves; on failure sets `error` and stays open. */
  submit: (args: { pinId: string; pin: string; reason?: string }) => Promise<void>
  /** Cancel — closes and resolves(false). */
  cancel: () => void
}

/** Verify a manager PIN. Demo build: fixed PIN. Real build: dhk8-platform-auth.
 *  Lane #172 — la prompt ahora recolecta `pinId` (6) + `pin` (6) en dos pasos
 *  para identificar al gerente (un negocio puede tener varios). Demo mode
 *  acepta cualquier pinId. */
async function verifyManagerPin(pinId: string, pin: string): Promise<{ ok: boolean; token?: string }> {
  if (PIN_GATE_DEMO_MODE) return { ok: pin === DEMO_MANAGER_PIN }
  // POST /v1/auth/manager-verify { pinId, pin } → { ok, user, token } / 401.
  // TCK-0016: capturamos el `token` (autorización de gerente, 90s) para mandarlo
  // a la mutation gateada como prueba verificable server-side.
  try {
    const body = await apiFetch<{ ok?: boolean; token?: string }>(
      svcUrl('dhk8-platform-auth', '/v1/auth/manager-verify'),
      { method: 'POST', body: { pinId, pin } },
    )
    return { ok: true, ...(typeof body?.token === 'string' ? { token: body.token } : {}) }
  } catch {
    return { ok: false }
  }
}

export const usePinGateStore = create<PinGateState>((set, get) => ({
  open: false,
  action: null,
  options: {},
  error: null,
  busy: false,
  _resolve: null,

  request(action, options = {}) {
    // If a previous prompt is somehow still open, resolve it as cancelled first.
    const prev = get()._resolve
    if (prev) prev({ approved: false })
    return new Promise<PinGateResult>((resolve) => {
      set({ open: true, action, options, error: null, busy: false, _resolve: resolve })
    })
  },

  async submit({ pinId, pin, reason }) {
    set({ busy: true, error: null })
    const { ok, token } = await verifyManagerPin(pinId, pin)
    if (ok) {
      const resolve = get()._resolve
      set({ open: false, action: null, options: {}, error: null, busy: false, _resolve: null })
      resolve?.({ approved: true, pinId, ...(reason !== undefined ? { reason } : {}), ...(token !== undefined ? { managerAuthToken: token } : {}) })
    } else {
      set({ busy: false, error: 'PIN incorrecto' })
    }
  },

  cancel() {
    const resolve = get()._resolve
    set({ open: false, action: null, options: {}, error: null, busy: false, _resolve: null })
    resolve?.({ approved: false })
  },
}))

/** Imperative entry point para handlers. Mantiene compat con call-sites legacy
 *  que esperan `boolean`: si el caller no necesita pinId/reason puede usar
 *  `await requestManagerPin('order.voidItem')` y comparar truthy.
 *
 *  Lane #172 — pasar `{ requireReason: true }` cuando la acción NECESITA un
 *  motivo escrito (ej: forzar liberar mesa, cortesía). El resultado trae
 *  `reason` poblado para audit/event payload. */
export function requestManagerPin(
  action: GatedAction,
  options?: PinGateOptions,
): Promise<PinGateResult> {
  return usePinGateStore.getState().request(action, options)
}

/** TCK-0016 — entrada rol-aware para acciones gateadas con autorización de gerente.
 *  - Si el usuario actual es gerencia (owner/admin/manager) → procede directo,
 *    SIN pedir PIN (no se le pide al dueño un PIN que ya posee). `managerAuthToken`
 *    undefined → el backend lo deja pasar porque su capability === true.
 *  - Si es cajero/mesero (manager_pin_required) → abre el prompt; si el gerente
 *    autoriza, devuelve el `managerAuthToken` verificable para la mutation. */
export async function requireManagerAuth(
  action: GatedAction,
  options?: PinGateOptions,
): Promise<{ proceed: boolean; managerAuthToken?: string; reason?: string }> {
  const role = useAuthStore.getState().user?.role ?? ''
  // Anular una orden completa SIEMPRE pide clave de gerente y motivo, aunque
  // quien opera sea gerente/dueño: deja rastro de quién y por qué (el backend
  // guarda cancelledByUserId desde el token de autorización).
  if (MANAGER_ROLES.has(role) && action !== 'order.voidOrder') return { proceed: true }
  const res = await requestManagerPin(action, options)
  if (!res.approved) return { proceed: false }
  return {
    proceed: true,
    ...(res.managerAuthToken !== undefined ? { managerAuthToken: res.managerAuthToken } : {}),
    ...(res.reason !== undefined ? { reason: res.reason } : {}),
  }
}
